import { fmtDate } from "../utils/helpers";

/**
 * "Jul 30" rather than "Jul 30, 2026". Repeating the current year on every row
 * is pure noise, and four rows of it makes a history read like a log file.
 */
function shortDate(ts) {
  const d = new Date(ts);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export default function RecentActivity({
  workouts,
  runs,
  onViewWorkout,
  onViewRun,
  onStartWorkout,
  onRepeatWorkout,
}) {
  const combined = [
    ...workouts.map((w) => ({ type: "workout", data: w, ts: w.startTime })),
    ...(runs || []).map((r) => ({ type: "run", data: r, ts: r.timestamp })),
  ]
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 3);

  if (combined.length === 0) {
    return (
      <section aria-label="Recent activity">
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
              <circle cx="12" cy="12" r="2.5" />
            </svg>
          </div>
          <div className="empty-title">Nothing here yet</div>
          <div className="empty-sub">
            Whenever you move, it&apos;ll turn up here.
          </div>
          <button className="btn btn-primary empty-cta" onClick={onStartWorkout}>
            Put something together
          </button>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Recent activity">
      {combined.map((item) => {
        if (item.type === "workout") {
          const w = item.data;
          const dur = w.endTime ? Math.round((w.endTime - w.startTime) / 60000) : null;
          const exCount = (w.exerciseBlocks || []).length;
          return (
            <div key={w.id} className="activity-card-wrap">
              <div
                className="activity-card"
                role="button"
                tabIndex={0}
                aria-label={`${w.name}, ${fmtDate(w.startTime)}${dur ? `, ${dur} minutes` : ""}`}
                onClick={() => onViewWorkout(w)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onViewWorkout(w);
                  }
                }}
              >
                <div className="activity-name">{w.name}</div>
                <div className="activity-meta">
                  {shortDate(w.startTime)}
                  {dur ? ` \u00B7 ${dur} min` : ""}
                  {` \u00B7 ${exCount} exercises`}
                </div>
              </div>
              {onRepeatWorkout && (
                <button
                  className="activity-repeat-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRepeatWorkout(w);
                  }}
                  aria-label={`Repeat ${w.name}`}
                  title="Do this workout again"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="17 1 21 5 17 9" />
                    <path d="M3 11V9a4 4 0 014-4h14" />
                    <polyline points="7 23 3 19 7 15" />
                    <path d="M21 13v2a4 4 0 01-4 4H3" />
                  </svg>
                </button>
              )}
            </div>
          );
        } else {
          const r = item.data;
          const mins = Math.round(r.duration / 60);
          return (
            <div key={r.id} className="activity-card-wrap">
              <div
                className="activity-card"
                role="button"
                tabIndex={0}
                aria-label={`${r.runType} Run, ${r.distance.toFixed(1)} ${r.unit || "km"}`}
                onClick={() => onViewRun(r)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onViewRun(r);
                  }
                }}
              >
                <div className="activity-name">{r.runType ? `${r.runType} run` : "Run"}</div>
                {/* Labelled, so nobody has to work out which number is which —
                    and never a bare figure: a run record without `unit` used to
                    print "3.1" between two labelled values, which reads as a
                    truncation bug rather than a distance. */}
                <div className="activity-meta">
                  {shortDate(r.timestamp)} &middot; {r.distance.toFixed(1)} {r.unit || "km"}{" "}
                  &middot; {mins} min
                </div>
              </div>
            </div>
          );
        }
      })}
    </section>
  );
}
